/**
 * API Key 管理服务
 * 用于管理租户级别的 API Key 配置
 */

import { apiGet, apiPost, apiPatch, apiDelete } from '@/lib/api';

// API Key 状态枚举
export enum ApiKeyStatus {
  ACTIVE = 'active',
  DISABLED = 'disabled',
}

// API Key 数据类型
export interface ApiKey {
  id: string;
  name: string;
  key_display?: string; // 仅在创建时返回完整 key
  key_preview?: string; // 脱敏后的 key，如 sk-d790e**********************5d70
  status: ApiKeyStatus;
  created_at: number; // Unix 时间戳
  updated_at?: number; // Unix 时间戳
  expires_at?: number; // Unix 时间戳，过期时间（后端字段名）
  expire_at?: number; // 兼容字段
  last_used_at?: number; // Unix 时间戳
}

// 创建 API Key 请求参数
export interface CreateApiKeyRequest extends Record<string, unknown> {
  name?: string;
  expires_at?: number; // Unix 时间戳，过期时间（后端使用 expires_at）
}

// 创建 API Key 响应（包含完整 key）
export interface CreateApiKeyResponse {
  id: string;
  name: string;
  plain_key?: string; // 完整 key，仅在创建时返回
  key_display?: string; // 脱敏的 key
  status: ApiKeyStatus;
  created_at: number;
  expires_at?: number; // Unix 时间戳，过期时间（后端使用 expires_at）
}

// 更新 API Key 请求参数
export interface UpdateApiKeyRequest extends Record<string, unknown> {
  name: string;
}

// API Key 列表响应
export interface ApiKeyListResponse {
  keys: ApiKey[];
}

/**
 * 获取 API Key 列表
 */
export const listApiKeys = async (): Promise<ApiKey[]> => {
  const response = await apiGet<ApiKeyListResponse>('/v1/tenant/api-keys');
  return response.keys || [];
};

/**
 * 创建 API Key
 */
export const createApiKey = async (
  data: CreateApiKeyRequest
): Promise<CreateApiKeyResponse> => {
  return apiPost<CreateApiKeyResponse>('/v1/tenant/api-keys', data);
};

/**
 * 更新 API Key
 */
export const updateApiKey = async (
  id: string,
  data: UpdateApiKeyRequest
): Promise<ApiKey> => {
  return apiPatch<ApiKey>(`/v1/tenant/api-keys/${id}`, data);
};

/**
 * 删除 API Key
 */
export const deleteApiKey = async (id: string): Promise<void> => {
  return apiDelete<void>(`/v1/tenant/api-keys/${id}`);
};

/**
 * 启用 API Key
 */
export const enableApiKey = async (id: string): Promise<ApiKey> => {
  return apiPatch<ApiKey>(`/v1/tenant/api-keys/${id}/enable`, {});
};

/**
 * 禁用 API Key
 */
export const disableApiKey = async (id: string): Promise<ApiKey> => {
  return apiPatch<ApiKey>(`/v1/tenant/api-keys/${id}/disable`, {});
};
